import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();
// localStorage flag persists "I'm browsing as a guest" across reloads, so the
// app does NOT auto-login the operator's preview-fallback admin on top of an
// explicit logout. Cleared on successful login/register.
const GUEST_FLAG = "radar-guest-mode";
const isGuestMode = () => typeof window !== "undefined" && window.localStorage?.getItem(GUEST_FLAG) === "1";
const setGuestMode = (v) => {
  try {
    if (v) window.localStorage.setItem(GUEST_FLAG, "1");
    else window.localStorage.removeItem(GUEST_FLAG);
  } catch (_) { /* ignore */ }
};

// --- Preview-safe axios: auto re-login on 401 and retry the original request.
// Disabled while in guest mode (so anonymous users get real 401s and see the
// "please sign in" UI instead of being silently auto-logged-in).
let _autoLoginInFlight = null;
const _autoLogin = async () => {
  if (isGuestMode()) throw new Error("guest-mode");
  if (!_autoLoginInFlight) {
    _autoLoginInFlight = axios
      .post(`${API}/auth/login`, { email: "admin@example.com", password: "admin123" }, { withCredentials: true })
      .finally(() => { _autoLoginInFlight = null; });
  }
  return _autoLoginInFlight;
};

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthUrl = url.includes("/api/auth/login") || url.includes("/api/auth/register") || url.includes("/api/auth/me") || url.includes("/api/auth/refresh");
    if (status === 401 && !original._retried && !isAuthUrl && !isGuestMode()) {
      original._retried = true;
      try {
        await _autoLogin();
        return axios(original);
      } catch (_) { /* fall-through */ }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Explicit guest mode → stay anonymous (no /auth/me call, no auto-login)
    if (isGuestMode()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      // Not signed in — let the visitor browse anonymously. They'll see
      // "Anmelden" CTAs on every interactive surface.
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    setGuestMode(false);
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name, role, opts = {}) => {
    setGuestMode(false);
    const payload = { email, password, name, role };
    if (opts.contractor_type) payload.contractor_type = opts.contractor_type;
    const res = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
    setUser(res.data);
    if (opts.acceptTerms) {
      try {
        await axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true });
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
      } catch (e) { console.warn("auto accept-terms failed", e); }
    }
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (_) { /* ignore */ }
    setGuestMode(true);
    setUser(null);
  };

  const updateRole = async (newRole) => {
    await axios.post(`${API}/auth/update-role`, { role: newRole }, { withCredentials: true });
    setUser(prev => prev ? { ...prev, role: newRole } : prev);
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch { setUser(null); }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateRole, refreshUser, setUser, isGuest: !user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
